import { useState } from "react";
import type { Reward } from "@/mocks/artistData";

interface Props {
  reward: Reward | null;
  artistId: string;
  onClose: () => void;
}

const categoryOptions = [
  { value: "offline", label: "오프라인 이벤트", icon: "ri-map-pin-line" },
  { value: "credit", label: "크레딧 등재", icon: "ri-film-line" },
  { value: "digital", label: "디지털 굿즈", icon: "ri-star-line" },
  { value: "exclusive", label: "독점 콘텐츠", icon: "ri-lock-line" },
];

const claimProcessInfo: Record<string, { icon: string; title: string; steps: string[]; color: string }> = {
  offline: {
    icon: "ri-map-pin-line",
    title: "오프라인 이벤트 수령 프로세스",
    steps: [
      "팬이 XP로 교환 신청",
      "이름 · 연락처 · 이메일 · 참석 날짜 입력",
      "이메일로 초청 확인서 발송",
      "크리에이터 측 최종 확정 후 안내",
    ],
    color: "#3d9be9",
  },
  credit: {
    icon: "ri-film-line",
    title: "크레딧 등재 수령 프로세스",
    steps: [
      "팬이 XP로 교환 신청",
      "크레딧 표기 이름 · 이메일 · SNS 계정 입력",
      "이메일로 공식 증명서 발송",
      "실제 크레딧에 이름 등재 후 알림",
    ],
    color: "#ffd60a",
  },
  digital: {
    icon: "ri-coupon-3-line",
    title: "디지털 굿즈 쿠폰 발행 프로세스",
    steps: [
      "팬이 XP로 교환 신청",
      "수령 이메일 입력",
      "즉시 쿠폰 코드 + 다운로드 링크 발송",
      "팬이 코드로 굿즈 다운로드",
    ],
    color: "#ff6eb4",
  },
  exclusive: {
    icon: "ri-key-2-line",
    title: "독점 콘텐츠 바우처 발행 프로세스",
    steps: [
      "팬이 XP로 교환 신청",
      "수령 이메일 입력",
      "즉시 접근 코드(바우처) 발송",
      "30일간 독점 콘텐츠 무제한 접근",
    ],
    color: "#c77dff",
  },
};

const badgeOptions = [
  { value: "", label: "없음" },
  { value: "LEGENDARY", label: "LEGENDARY" },
  { value: "ULTRA RARE", label: "ULTRA RARE" },
  { value: "RARE", label: "RARE" },
];

export default function RewardFormModal({ reward, onClose }: Props) {
  const isEdit = !!reward;
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    title: reward?.title ?? "",
    description: reward?.description ?? "",
    category: reward?.category ?? "offline",
    xpCost: reward?.xpCost ?? 10000,
    totalStock: reward?.totalStock ?? 20,
    badge: reward?.badge ?? "",
    startDate: reward?.startDate ?? "",
    endDate: reward?.endDate ?? "",
    perks: reward?.perks.join("\n") ?? "",
    isLimited: reward?.isLimited ?? false,
  });

  const handleChange = (key: string, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSaved(true);
  };

  const claimInfo = claimProcessInfo[form.category];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-[#12121a] border border-white/10 overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <h2 className="text-white font-black text-base">{isEdit ? "리워드 수정" : "리워드 추가"}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-white/50 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        {saved ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#39ff14]/20 mx-auto mb-4">
              <i className="ri-check-double-line text-[#39ff14] text-2xl" />
            </div>
            <h3 className="text-white font-black text-lg mb-2">저장 완료!</h3>
            <p className="text-white/40 text-sm mb-6">리워드가 성공적으로 {isEdit ? "수정" : "추가"}되었습니다.</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-white/10 text-white font-semibold text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              닫기
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Category */}
            <div>
              <label className="text-white/50 text-xs font-semibold block mb-2">리워드 종류</label>
              <div className="grid grid-cols-2 gap-2">
                {categoryOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleChange("category", opt.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer text-left ${
                      form.category === opt.value
                        ? "bg-white/10 border-white/30 text-white"
                        : "bg-white/5 border-white/10 text-white/40 hover:text-white/70"
                    }`}
                  >
                    <i className={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 수령 프로세스 안내 */}
            {claimInfo && (
              <div
                className="p-4 rounded-xl border space-y-3"
                style={{ background: `${claimInfo.color}08`, borderColor: `${claimInfo.color}25` }}
              >
                <div className="flex items-center gap-2">
                  <i className={`${claimInfo.icon} text-sm`} style={{ color: claimInfo.color }} />
                  <span className="text-xs font-bold" style={{ color: claimInfo.color }}>{claimInfo.title}</span>
                </div>
                <div className="space-y-1.5">
                  {claimInfo.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span
                        className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 mt-0.5"
                        style={{ background: `${claimInfo.color}25`, color: claimInfo.color }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-white/50 text-xs leading-relaxed">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="text-white/50 text-xs font-semibold block mb-2">리워드 제목 *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="예: 팬미팅 초청권, 뮤직비디오 크레딧..."
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-white/50 text-xs font-semibold block mb-2">상세 설명 *</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="리워드에 대한 상세 설명을 입력하세요..."
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30 resize-none"
              />
              <div className="text-right text-white/20 text-xs mt-1">{form.description.length}/500</div>
            </div>

            {/* XP Cost & Stock */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-semibold block mb-2">필요 XP</label>
                <div className="relative">
                  <i className="ri-coin-fill absolute left-3 top-1/2 -translate-y-1/2 text-[#ffd60a] text-sm" />
                  <input
                    type="number"
                    value={form.xpCost}
                    onChange={(e) => handleChange("xpCost", Number(e.target.value))}
                    min={100}
                    step={100}
                    className="w-full pl-8 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-semibold block mb-2">발행 수량</label>
                <input
                  type="number"
                  value={form.totalStock}
                  onChange={(e) => handleChange("totalStock", Number(e.target.value))}
                  min={1}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-white/30"
                />
              </div>
            </div>

            {/* Perks */}
            <div>
              <label className="text-white/50 text-xs font-semibold block mb-2">포함 혜택 (한 줄에 하나씩)</label>
              <textarea
                value={form.perks}
                onChange={(e) => handleChange("perks", e.target.value)}
                placeholder={"팬미팅 입장권\n2샷 포토카드\n사인 CD"}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30 resize-none font-mono"
              />
            </div>

            {/* Badge & 제공 일정 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-semibold block mb-2">희귀도 배지</label>
                <div className="relative">
                  <select
                    value={form.badge}
                    onChange={(e) => handleChange("badge", e.target.value)}
                    className="w-full appearance-none px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-white/30 cursor-pointer"
                  >
                    {badgeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-semibold block mb-2">한정 리워드 여부</label>
                <div className="flex items-center h-[46px] gap-3 px-4 rounded-xl bg-white/5 border border-white/10">
                  <span className="text-white/40 text-sm flex-1">한정 리워드</span>
                  <button
                    onClick={() => handleChange("isLimited", !form.isLimited)}
                    className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${form.isLimited ? "bg-[#39ff14]" : "bg-white/20"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.isLimited ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* 제공 일정 */}
            <div>
              <label className="text-white/50 text-xs font-semibold block mb-2">제공 일정 *</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/30 text-[10px] block mb-1.5">시작일</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-white/30 cursor-pointer"
                    style={{ colorScheme: "dark" }}
                  />
                </div>
                <div>
                  <label className="text-white/30 text-[10px] block mb-1.5">종료일</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => handleChange("endDate", e.target.value)}
                    min={form.startDate}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-white/30 cursor-pointer"
                    style={{ colorScheme: "dark" }}
                  />
                </div>
              </div>
              {form.startDate && form.endDate && (
                <div className="mt-2 flex items-center gap-2 text-white/30 text-xs">
                  <i className="ri-information-line" />
                  <span>기간 내에만 팬이 교환 신청할 수 있습니다</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/60 font-semibold text-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || !form.description.trim()}
                className="flex-1 py-3 rounded-xl bg-[#ffd60a] text-[#0a0a0f] font-black text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {isEdit ? "수정 저장" : "리워드 추가"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
